import { FC, useMemo } from "react";
import {
  Bar,
  Cell,
  LabelList,
  BarChart as RechartsBarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Center, Text } from "@/components/ui";
import { useLocalization } from "@/hooks";

export interface CenteredHorizontalBarDataPoint {
  label: string;
  tooltipLine?: string;
  value: number;
}

interface CenteredHorizontalBarChartProps {
  formatValue?: (v: number) => string;
  height?: number;
  /** Minimum chart width in px. The chart fills the available width but
      never shrinks below this — it scrolls horizontally instead. */
  minWidth?: number;
  negativeColor?: string;
  points: CenteredHorizontalBarDataPoint[];
  positiveColor?: string;
}

const TICK_MUTED = { fontSize: 11, fill: "hsl(220,10%,46%)" };
const TICK_DARK = { fontSize: 11, fill: "hsl(222,20%,16%)" };
const MAX_LABEL_CHARS = 30;
const DEFAULT_POSITIVE = "hsl(160, 84%, 39%)";
const DEFAULT_NEGATIVE = "hsl(0, 75%, 60%)";

function CenteredTooltip({
  active,
  formatValue,
  payload,
}: {
  active?: boolean;
  formatValue: (v: number) => string;
  payload?: {
    payload: {
      name: string;
      tooltipLine?: string;
      value: number;
    };
  }[];
}) {
  if (!active || !payload?.length) return null;
  const { name, tooltipLine, value } = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        maxWidth: 240,
        padding: "8px 12px",
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {name}
      </p>
      <p
        style={{
          color: value >= 0 ? DEFAULT_POSITIVE : DEFAULT_NEGATIVE,
          fontSize: 12,
          margin: "2px 0 0",
        }}
      >
        {tooltipLine ?? formatValue(value)}
      </p>
    </div>
  );
}

const CenteredHorizontalBarChart: FC<CenteredHorizontalBarChartProps> = ({
  formatValue = (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}`,
  height,
  minWidth = 520,
  negativeColor = DEFAULT_NEGATIVE,
  points,
  positiveColor = DEFAULT_POSITIVE,
}) => {
  const { i18n } = useLocalization();

  const { chartData, chartHeight, domain, leftPadding } = useMemo(() => {
    if (points.length === 0) {
      return {
        chartData: [],
        chartHeight: 300,
        domain: [-1, 1],
        leftPadding: 112,
      };
    }

    const sorted = [...points].sort((a, b) => b.value - a.value);
    const barHeight = sorted.length <= 6 ? 36 : 28;
    const calculatedHeight = Math.max(sorted.length * barHeight + 60, 240);
    const maxLabelLength = Math.max(...sorted.map((p) => p.label.length));
    const calculatedLeftPadding = Math.min(
      Math.max(112, Math.min(maxLabelLength, MAX_LABEL_CHARS) * 6.2),
      200
    );

    const max = Math.max(...sorted.map((p) => Math.abs(p.value)));
    const padded = max * 1.18;
    const symmetric: [number, number] = [-padded, padded];

    const data = sorted.map((p) => ({
      displayName:
        p.label.length > MAX_LABEL_CHARS
          ? `${p.label.substring(0, MAX_LABEL_CHARS)}…`
          : p.label,
      name: p.label,
      tooltipLine: p.tooltipLine,
      value: p.value,
    }));

    return {
      chartData: data,
      chartHeight: calculatedHeight,
      domain: symmetric,
      leftPadding: calculatedLeftPadding,
    };
  }, [points]);

  if (chartData.length === 0) {
    return (
      <Center className="h-[300px] w-full">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  const sharedChartProps = {
    data: chartData,
    layout: "vertical" as const,
    margin: { bottom: 20, left: 10, right: 32, top: 10 },
  };

  const sharedChildren = (
    <>
      <XAxis
        axisLine={false}
        domain={domain}
        tick={TICK_MUTED}
        tickCount={5}
        tickFormatter={(v) => formatValue(v)}
        tickLine={false}
        type="number"
      />
      <YAxis
        dataKey="displayName"
        tick={TICK_DARK}
        tickLine={false}
        type="category"
        width={leftPadding}
      />
      <ReferenceLine stroke="hsl(220,14%,82%)" strokeWidth={1.5} x={0} />
      <Tooltip
        content={<CenteredTooltip formatValue={formatValue} />}
        cursor={{ fill: "hsl(220,14%,97%)" }}
      />
      <Bar barSize={chartData.length <= 6 ? 22 : 16} dataKey="value" radius={4}>
        {chartData.map((d, i) => (
          <Cell
            fill={d.value >= 0 ? positiveColor : negativeColor}
            key={`cell-${i}`}
          />
        ))}
        <LabelList
          dataKey="value"
          fill="hsl(222,20%,16%)"
          fontSize={11}
          fontWeight={600}
          formatter={(v) => formatValue(Number(v))}
          position="right"
        />
      </Bar>
    </>
  );

  const finalHeight = height ?? chartHeight;
  const needsScroll = finalHeight > 520;
  const displayHeight = needsScroll ? 520 : finalHeight;

  /* Plain `<div>`s for the scroll wrappers (not RN `<View>`/`<Box>`): RN Web
     doesn't expose `overflowX`. The outer wrapper scrolls horizontally and
     `minWidth: 0` lets it shrink below the inner content so the surrounding
     card keeps its flex share; the inner wrapper enforces the chart's minimum
     width and scrolls vertically when there are many bars. */
  return (
    <div style={{ minWidth: 0, overflowX: "auto", width: "100%" }}>
      <div
        style={{
          height: displayHeight,
          minWidth,
          overflowY: needsScroll ? "auto" : "visible",
          width: "100%",
        }}
      >
        <ResponsiveContainer height={finalHeight} minWidth={0} width="100%">
          <RechartsBarChart {...sharedChartProps}>
            {sharedChildren}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CenteredHorizontalBarChart;
